// Approval chains — what every Send host shares: collect each chain panel's
// readiness (Send stays disabled while any chain item has a problem), and list
// every problem of a 422 send refusal (D4: the server refuses the whole send,
// nothing goes out partially, and `errors[]` names each problem).

import { useCallback, useState } from "react";
import { ApiError } from "@/api/client";
import type { ChainReadiness } from "./ApprovalChainComposerPanel";

export function useChainReadiness() {
  const [byItem, setByItem] = useState<Record<string, ChainReadiness>>({});
  const report = useCallback((itemId: string, readiness: ChainReadiness) => {
    setByItem((prev) => {
      const before = prev[itemId];
      if (
        before &&
        before.ready === readiness.ready &&
        before.reasons.join("\n") === readiness.reasons.join("\n")
      ) {
        return prev;
      }
      return { ...prev, [itemId]: readiness };
    });
  }, []);
  /** Readiness over the chain items currently in the package. An item that
   *  has not reported yet holds Send. */
  const summarize = useCallback(
    (itemIds: readonly string[]): ChainReadiness => {
      const reasons: string[] = [];
      for (const id of itemIds) {
        const r = byItem[id];
        if (!r) reasons.push("Checking the approval chain…");
        else reasons.push(...r.reasons);
      }
      const unique = [...new Set(reasons)];
      return { ready: unique.length === 0, reasons: unique };
    },
    [byItem],
  );
  return { report, summarize };
}

/** The problems of a send the server refused with 422, or null for any other
 *  failure. */
export function sendBlockedProblems(err: unknown): string[] | null {
  if (err instanceof ApiError && err.status === 422) {
    const problems = err.errors.filter((e) => e.trim().length > 0);
    return problems.length > 0 ? problems : [err.message];
  }
  return null;
}

export function SendBlockedList({
  title,
  problems,
  testId,
}: {
  title: string;
  problems: readonly string[];
  testId: string;
}) {
  if (problems.length === 0) return null;
  return (
    <div
      role="alert"
      className="rounded-md border border-status-error bg-status-error/5 p-space-3 flex flex-col gap-space-1"
      data-testid={testId}
    >
      <span className="text-small font-semibold text-onyx">{title}</span>
      <ul className="m-0 pl-space-5 text-small text-onyx">
        {problems.map((p) => (
          <li key={p}>{p}</li>
        ))}
      </ul>
    </div>
  );
}
